import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageLoading } from "@/components/layout/Layout";
import { ArrowLeft, Scales, Grains, ChartLineUp, Clock, Drop } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useT } from "@/contexts/I18nContext";
import {
    AnimalHeader,
    AnimalCycleStats,
    AnimalReportsTable,
    AnimalWeighingsTable,
    AnimalMilkTable,
    AnimalFeedEventsTable,
} from "@/components/AnimalDetailParts";

export default function AnimalDetail() {
    const { t } = useT();
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [reports, setReports] = useState([]);
    const [events, setEvents] = useState([]);
    const [weighings, setWeighings] = useState([]);
    const [milk, setMilk] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, r, ev, wg, mk] = await Promise.all([
                api.get(`/animals/${id}/current`),
                api.get(`/reports`, { params: { animal_id: id } }),
                api.get(`/feed/events`, { params: { animal_id: id, limit: 100 } }),
                api.get(`/weighings`, { params: { animal_id: id, limit: 100 } }),
                api.get(`/milk`, { params: { animal_id: id, limit: 100 } }).catch(() => ({ data: [] })),
            ]);
            setData(c.data);
            setReports(r.data);
            setEvents(ev.data);
            setWeighings(wg.data);
            setMilk(mk.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <PageLoading />;
    if (!data) return <div>—</div>;

    const animal = data.animal;
    const isMilk = animal.production_type === "milk";

    return (
        <div className="space-y-6" data-testid="animal-detail-page">
            <Link
                to="/animales"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-[#1A3626] transition-colors"
            >
                <ArrowLeft size={16} /> {t("back_to_animals")}
            </Link>

            <AnimalHeader animal={animal} isMilk={isMilk} />

            <section>
                <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2">
                    <Clock size={18} /> {t("current_cycle")}
                </h2>
                <AnimalCycleStats animal={animal} data={data} isMilk={isMilk} />
            </section>

            <Tabs defaultValue="reports" className="space-y-4">
                <TabsList className="bg-[#E4EAE1]">
                    <TabsTrigger value="reports" data-testid="tab-reports">
                        <ChartLineUp size={16} className="mr-2" /> {t("tab_reports")}
                    </TabsTrigger>
                    {isMilk ? (
                        <TabsTrigger value="milk" data-testid="tab-milk">
                            <Drop size={16} className="mr-2" /> {t("tab_milk")}
                        </TabsTrigger>
                    ) : (
                        <TabsTrigger value="weighings" data-testid="tab-weighings">
                            <Scales size={16} className="mr-2" /> {t("tab_weighings")}
                        </TabsTrigger>
                    )}
                    <TabsTrigger value="events" data-testid="tab-events">
                        <Grains size={16} className="mr-2" /> {t("tab_events")}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="reports">
                    <AnimalReportsTable reports={reports} />
                </TabsContent>

                {!isMilk && (
                    <TabsContent value="weighings">
                        <AnimalWeighingsTable weighings={weighings} />
                    </TabsContent>
                )}

                {isMilk && (
                    <TabsContent value="milk">
                        <AnimalMilkTable milk={milk} />
                    </TabsContent>
                )}

                <TabsContent value="events">
                    <AnimalFeedEventsTable events={events} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
